import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import baseURL from "../../authComponents/baseURL/BaseURL";
import avatarFemale from "../../../assets/images/avatarfemal1.jpg";
import avatarMale from "../../../assets/images/avatatmale1.jpg";
import loginToken from "../../authComponents/localStorageItems/LoginTokin";
import { authSliceActions } from "../../../redux/slices/authSlice";

// Helper function to format strings from snake_case to Title Case
const getProperString = (string) => {
  if (!!string) {
    return string
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  } else {
    return "--";
  }
};

const OtherProfiles = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("personal");
  const { id } = useParams();
  const dispatch = useDispatch();
  const getStatus = useSelector((store) => store.authSlice.getStatus);

  // Fetch user profile and status on component mount
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await baseURL.get(`/admin/users/details/${id}`);
        setProfileData(response.data.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Fetch the user's auth status on component mount
    const fetchUserStatus = async () => {
      if (loginToken) {
        try {
          const response = await baseURL.get(`/profile`, {
            headers: {
              Authorization: `Bearer ${loginToken}`,
            },
          });
          dispatch(authSliceActions.getStatus(response.data.data.status));
        } catch (error) {
          if (error.response && error.response.status === 401) {
            localStorage.removeItem("authToken");
            // Don't reload the page here, just handle the error
          }
          console.error(
            "Error fetching profile status:",
            error.response?.data || error.message
          );
        }
      }
    };

    fetchProfileData();
    fetchUserStatus(); // Get user status when component mounts
  }, [id, dispatch]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-gray-800">Error</h2>
          <p className="text-gray-600 mt-2">
            Failed to load profile data: {error}
          </p>
        </div>
      </div>
    );
  }

  const fullName =
    profileData?.first_name && profileData?.last_name
      ? `${profileData.first_name} ${profileData.last_name}`
      : "Profile Name";

  const height =
    profileData?.feet != null || profileData?.inch != null
      ? `${profileData?.feet ?? ""}'${profileData?.inch ?? ""}"`
      : "Not specified";

  const TabButton = ({ name, label }) => (
    <button
      onClick={() => setActiveTab(name)}
      className={`px-4 py-2 font-medium rounded-t-lg transition-all ${
        activeTab === name
          ? "bg-white text-rose-600 border-t border-l border-r"
          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 font-sans">
      <div className="max-w-5xl mx-auto">
        {/* Profile Header */}
        <div className="bg-gradient-to-r from-rose-500 to-pink-500 rounded-t-xl p-8 shadow-md">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="relative">
              <div className="h-32 w-32 rounded-full bg-white p-1 shadow-lg">
                <img
                  src={
                    profileData?.image_url
                      ? profileData.image_url
                      : profileData?.gender === "male"
                      ? avatarMale
                      : avatarFemale
                  }
                  alt="Profile Avatar"
                  className="rounded-full w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="text-center md:text-left">
              <h1 className="text-3xl font-serif font-bold text-white">
                {fullName} ( {getProperString(profileData.occupation)} )
              </h1>
              <p className="text-rose-100 mt-1">
                {profileData?.title || "Title not specified"}
              </p>
              {getStatus !== "active" && (
                <div className="mt-3 bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-sm font-medium inline-block">
                  Account pending approval
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Profile Content */}
        <div className="bg-white rounded-b-xl shadow-md">
          {/* Tabs */}
          <div className="flex border-b overflow-x-auto no-scrollbar">
            <div className="px-4 pt-4 flex gap-2">
              <TabButton name="personal" label="Personal Info" />
              <TabButton name="contact" label="Contact Details" />
              <TabButton name="education" label="Education & Career" />
              {/* <TabButton name="bio" label="Bio" /> */}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Personal Info Tab */}
            {activeTab === "personal" && (
              <div className="space-y-6">
                <h3 className="text-xl font-serif font-semibold text-gray-800 border-b border-gray-200 pb-2">
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoItem label="Age" value={profileData?.age} />
                  <InfoItem label="Gender" value={getProperString(profileData?.gender)} />
                  <InfoItem
                    label="Marital Status"
                    value={getProperString(profileData?.marital_status)}
                  />
                  <InfoItem label="Religion" value={getProperString(profileData?.religion)} />
                  <InfoItem label="Caste" value={profileData?.caste} />
                  <InfoItem label="Height" value={height} />
                  <InfoItem label="Complexion" value={getProperString(profileData?.look)} />
                </div>
              </div>
            )}

            {/* Contact Details Tab */}
            {activeTab === "contact" && (
              <div className="space-y-6">
                <h3 className="text-xl font-serif font-semibold text-gray-800 border-b border-gray-200 pb-2">
                  Contact Details
                </h3>
                {getStatus === "active" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoItem label="Phone" value={profileData?.mobile} />
                    <InfoItem label="Email" value={profileData?.email} />
                    <InfoItem label="Address" value={profileData?.country} />
                  </div>
                ) : (
                  <div className="bg-rose-50 border border-rose-200 text-rose-700 p-4 rounded-lg">
                    <p className="flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Your account is currently not approved. Please check your
                      email for account approval instructions.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Education & Career Tab */}
            {activeTab === "education" && (
              <div className="space-y-6">
                <h3 className="text-xl font-serif font-semibold text-gray-800 border-b border-gray-200 pb-2">
                  Education
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoItem
                    label="Highest Degree"
                    value={getProperString(profileData?.education)}
                  />
                </div>

                <h3 className="text-xl font-serif font-semibold text-gray-800 border-b border-gray-200 pb-2 mt-8">
                  Career
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoItem
                    label="Occupation"
                    value={getProperString(profileData?.occupation)}
                  />
                  <InfoItem label="Annual Income" value={profileData?.income} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable component for displaying information items
const InfoItem = ({ label, value }) => (
  <div className="bg-gray-50 p-3 rounded-lg">
    <p className="text-sm text-gray-500 mb-1">{label}</p>
    <p className="text-gray-800 font-medium">{value ?? "Not specified"}</p>
  </div>
);

export default OtherProfiles;
